import type { CropRect } from '@@/types/api/common';

/**
 * Coerce a focal-point coordinate to a valid value in [0, 1]. Non-finite,
 * NaN, or out-of-range inputs collapse to `0.5` (center) so that a partially
 * restored edit-mode state never propagates bad values into a crop rect.
 */
function normalizeFocalCoord(value: number): number {
  if (!Number.isFinite(value) || value < 0 || value > 1) {
    return 0.5;
  }
  return value;
}

/**
 * Compute the largest aspect-locked crop rectangle that fits inside `[0, 1]²`,
 * centered on `focal`, then clamped to bounds. Coordinates are fractional in
 * oriented-pixel space.
 *
 * The fractional `[0, 1]²` space is stretched over the image's actual pixel
 * dimensions, so a rect that's "square" in fractional space is only a square
 * of pixels when the image itself is square. The `natural` parameter lets us
 * account for the image's real aspect ratio when picking `w` and `h`:
 *
 *   k = (aspect.w / aspect.h) * (natural.h / natural.w)
 *
 * gives the ratio between fractional width and height needed to hit the
 * requested pixel aspect. `k ≤ 1` → the fractional rect is tall/narrow;
 * `k > 1` → it's wide/short.
 *
 * Input handling:
 * - NaN / non-finite / out-of-range focal components are coerced to 0.5
 *   (see `normalizeFocalCoord`).
 * - Degenerate `natural` dimensions (0 or negative — typical before the
 *   image has loaded) fall back to the full frame `{x:0, y:0, w:1, h:1}`,
 *   which the store will replace once real dimensions arrive.
 * - The final `x`/`y` are always clamped to `[0, 1 - w]` / `[0, 1 - h]`, so
 *   this function never throws on out-of-range input — it degrades to a
 *   sensible centered/edge-aligned rectangle.
 */
export function computeAutoCrop(
  focal: { x: number; y: number },
  aspect: { w: number; h: number },
  natural: { w: number; h: number },
): CropRect {
  const fx = normalizeFocalCoord(focal.x);
  const fy = normalizeFocalCoord(focal.y);

  // Guard against degenerate natural dimensions (before image loads).
  if (natural.w <= 0 || natural.h <= 0) {
    return { x: 0, y: 0, w: 1, h: 1 };
  }

  // k = target-aspect-ratio × (naturalH / naturalW).
  // If k ≤ 1 the fractional rect is tall/narrow; else wide/short.
  const k = (aspect.w / aspect.h) * (natural.h / natural.w);
  const w = k <= 1 ? k : 1;
  const h = k <= 1 ? 1 : 1 / k;

  let x = fx - w / 2;
  let y = fy - h / 2;
  x = Math.max(0, Math.min(1 - w, x));
  y = Math.max(0, Math.min(1 - h, y));

  return { x, y, w, h };
}

/**
 * Initial cover-art editor state: focal at center, both auto-crops computed
 * using `natural` (or falling back to full-frame placeholders when the image
 * hasn't loaded yet), and no per-aspect overrides. Consumed by the cover-art
 * store on init and `$reset`. The store passes its own `natural` state and
 * recomputes crops when real dimensions arrive via `setNaturalDimensions`.
 */
export function defaultCoverArtState(
  natural: { w: number; h: number } = { w: 0, h: 0 },
): {
  focalPoint: { x: number; y: number };
  natural: { w: number; h: number };
  crops: { '16_9': CropRect; '1_1': CropRect };
  overrides: { '16_9': boolean; '1_1': boolean };
} {
  const focal = { x: 0.5, y: 0.5 };
  return {
    focalPoint: focal,
    natural,
    crops: {
      '16_9': computeAutoCrop(focal, { w: 16, h: 9 }, natural),
      '1_1': computeAutoCrop(focal, { w: 1, h: 1 }, natural),
    },
    overrides: { '16_9': false, '1_1': false },
  };
}
